import { FocusScope } from "@react-aria/focus"
import PropTypes from "prop-types"
import { Fragment, memo, useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"

import {
  updateChoice,
  fetchStylecode,
  trackSlide,
} from "areas/product/store/actions"
import Button from "components/ui/button"
import { jewlr_t } from "helpers/application"
import { useNavigation } from "helpers/use-navigation"
import { cn } from "styles/utils"

import Stones from "./stones"

const StoneCategories = memo(
  ({ categories, handleCategoryClick, pref, selectedCategory }) => {
    const keyboardProps = useNavigation({ direction: "both" })

    return (
      <div
        aria-label="Stone categories"
        className="grid grid-cols-2 gap-10"
        role="tablist"
      >
        {categories
          .slice()
          .reverse()
          .map((category, index) => {
            const selected = selectedCategory.title === category.title

            return (
              <Fragment key={category.title}>
                <Button
                  aria-controls={`${pref}-${category.title}-tabpanel`}
                  aria-selected={selected}
                  className={cn(
                    "text-12 h-34 w-full rounded-full font-semibold",
                    selected
                      ? "bg-black text-white"
                      : "border-black-print text-black-print border bg-white"
                  )}
                  data-cy={`product-wizard-option-${pref}-category-${category.type}`}
                  id={`${pref}-${category.title}-tab`}
                  onClick={() => handleCategoryClick(category)}
                  role="tab"
                  tabIndex={
                    (selectedCategory ? selected : index === 0) ? 0 : -1
                  }
                  {...keyboardProps}
                >
                  {category.title}{" "}
                  {category.title === "Gemstones"
                    ? `(+${jewlr_t("number.currency.format.unit")})`
                    : ""}
                </Button>
              </Fragment>
            )
          })}
      </div>
    )
  }
)

StoneCategories.displayName = "StoneCategories"

StoneCategories.propTypes = {
  categories: PropTypes.array,
  handleCategoryClick: PropTypes.func,
  pref: PropTypes.string,
  selectedCategory: PropTypes.object,
}

const StoneOption = ({ handleCategoryClick, pref, selectedCategory }) => {
  const dispatch = useDispatch()
  const wizardData = useSelector((state) => state.wizard.data[pref])
  const selectedChoice = useSelector((state) => state.choices?.[pref])

  const setChoice = useCallback(
    (option) => {
      dispatch(updateChoice(pref, option, true))

      // Style code switcher
      if (option.style_code) {
        dispatch(fetchStylecode(option.style_code))
      } else if (option.track_slide) {
        dispatch(trackSlide(option.track_slide))
      }
    },
    [dispatch, pref]
  )

  if (!wizardData || !selectedCategory) return null

  return (
    <>
      <FocusScope>
        <StoneCategories
          categories={wizardData.categories}
          handleCategoryClick={handleCategoryClick}
          pref={pref}
          selectedCategory={selectedCategory}
        />
      </FocusScope>
      <div
        aria-labelledby={`${pref}-${selectedCategory.title}-tab`}
        className="mt-16"
        id={`${pref}-${selectedCategory.title}-tabpanel`}
        role="tabpanel"
      >
        <FocusScope>
          <Stones
            headingTitle={wizardData.heading?.title}
            options={selectedCategory.options}
            pref={pref}
            selectedChoice={selectedChoice}
            setChoice={setChoice}
          />
        </FocusScope>
      </div>
    </>
  )
}

StoneOption.propTypes = {
  handleCategoryClick: PropTypes.func,
  pref: PropTypes.string,
  selectedCategory: PropTypes.object,
}

export default StoneOption
